import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ContextMenu, { MenuOption } from '../common/ContextMenu';
import { ContextMenuWrapperProps, IgnoredErrorInfo } from './SpreadsheetTypes';
import { MarkDispatchContext } from '../../context/MarkContext';
import { validationTooltipMessage } from '../../utils/validationUtils';
import { useGetRawValue } from '../hooks/useGetRawValue';
import { useRetryValidation, VALIDATING_TOOLTIP } from '../hooks/useValidationFetchState';
import { useTheme } from '@mui/material';
import { useAtom } from 'jotai';
import {
  showUpdateAllExtractionsModalAtom,
  validationDismissalDialogAtom,
} from '../../atoms/spreadsheet-atoms';
import { IValidation } from '../../types/validation-types';
import { AppContext } from '../../App';

const activeStyle = {
  textDecoration: 'underline',
};

const ContextMenuWrapper: React.FC<ContextMenuWrapperProps> = ({
  extractionId,
  isErrorIgnored,
  validationMessage,
  contextMenu,
  closeContextMenu,
  field,
  cellValue,
  validationResponse,
  dpid,
  validating = false,
  validationNotReady = false,
  // isCellHighlighted,
  // setIsCellHighlighted,
}) => {
  const theme = useTheme();

  const iconActiveStyle = {
    color: theme.palette.primary.main,
  };

  const { data: rawValue, isLoading, isError } = useGetRawValue(extractionId);
  const extractedValueLabel = isLoading
    ? 'Loading...'
    : rawValue?.results?.[field]?.content || 'No data available';
  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const { setToast } = useContext(AppContext);
  const [, setShowUpdateAllExtractionsModal] = useAtom(showUpdateAllExtractionsModalAtom);
  const [, setValidationDismissalDialog] = useAtom(validationDismissalDialogAtom);
  const retryValidation = useRetryValidation();

  // Determine dismissal reason (if any) for this field
  const dismissalReasonForField = useMemo(() => {
    const fieldErrors = validationResponse?.validationErrorDetails?.filter(
      (v) => v.field === field
    );
    return fieldErrors?.find((v) => v.dismissalReason)?.dismissalReason ?? null;
  }, [validationResponse, field]);

  const handleOpenDismissalDialog = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      if (!dpid) {
        console.error('Data pool ID not found');
        setToast({
          variant: 'error',
          open: true,
          message: 'Data pool ID not found',
        });
        return;
      }

      // Find the validation error from validationErrorDetails by matching the field
      const validationError = validationResponse?.validationErrorDetails?.find(
        (v) => v.field === field
      );

      if (!validationError) {
        console.error('Could not find validation error for field:', field);
        setToast({
          variant: 'error',
          open: true,
          message: 'Could not find validation error for this field',
        });
        return;
      }

      setValidationDismissalDialog({
        isOpen: true,
        dataPoolId: dpid,
        validationId: validationError.id,
        extractionId,
        field,
        currentDismissalReason: validationError.dismissalReason || null,
        error: validationError.error || null,
        errorDetail: validationError.errorDetail || null,
      });
    },
    [extractionId, field, validationResponse, dpid, setValidationDismissalDialog, setToast]
  );

  // const toggleHighlightCell = (event: React.MouseEvent): void => {
  //   event.preventDefault();
  //   event.stopPropagation();
  //   // Logic for Highlight Cells
  //   setIsCellHighlighted(!isCellHighlighted);
  // };

  const menuOptions: MenuOption[] = useMemo(() => {
    const options: MenuOption[] = [
      // Option 2: Extracted Value
      {
        label: extractedValueLabel,
        prependTitle: 'Extracted',
        nonClickable: true,
        dataCy: 'ctx-menu-item-extracted-value',
      },
      // Validation refetch state of the row's extraction: the last result is stale, so the
      // dismiss option is not offered; an exhausted poll offers a retry instead.
      ...(validating
        ? [
            { isDivider: true },
            {
              label: VALIDATING_TOOLTIP,
              prependTitle: 'Validation',
              nonClickable: true,
              dataCy: 'ctx-menu-item-validating',
            },
          ]
        : []),
      ...(validationNotReady
        ? [
            { isDivider: true },
            {
              label: 'Retry Validation',
              onClick: () => retryValidation(extractionId),
              dataCy: 'ctx-menu-item-retry-validation',
            },
          ]
        : []),
      // Option 3: Add "Dismiss Error" option if there's an error
      ...(validationMessage || isErrorIgnored
        ? [
            { isDivider: true },
            {
              label: isErrorIgnored
                ? `Error Dismissed${dismissalReasonForField ? ` (${dismissalReasonForField})` : ''} - ${validationMessage}`
                : `Dismiss Error: ${validationMessage}`,
              onClick: handleOpenDismissalDialog,
              dataCy: 'ctx-menu-item-dismiss-error',
            },
          ]
        : []),
      { isDivider: true },
      // Option 4: "Copy Value to Clipboard"
      {
        label: 'Copy To Clipboard',
        onClick: () => {
          navigator.clipboard.writeText(cellValue);
        },
        dataCy: 'ctx-menu-item-copy-to-clipboard',
      },
      {
        label: 'Edit Field In List',
        onClick: () => {
          dispatch({
            type: 'FOCUS_FIELD',
            payload: { field: field, fieldListFocused: true },
          });
        },
        dataCy: 'ctx-menu-item-edit-field-in-list',
      },
      { isDivider: true },
      {
        label: 'Update All Extractions',
        onClick: () => {
          setShowUpdateAllExtractionsModal(true);
        },
        dataCy: 'ctx-menu-item-update-all-extractions',
      },
      // // Option 4: "Highlight cell to review later"
      // {
      //   label: 'Highlight cell to review later',
      //   onClick: toggleHighlightCell,
      //   icon: isCellHighlighted ? <CheckIcon style={iconActiveStyle} /> : <CloseOutlinedIcon />,
      //   style: isCellHighlighted ? activeStyle : {},
      // },
    ];

    return options;
  }, [
    validationMessage,
    isErrorIgnored,
    extractedValueLabel,
    handleOpenDismissalDialog,
    validating,
    validationNotReady,
    retryValidation,
    extractionId,
  ]);

  return (
    <ContextMenu
      x={contextMenu.x}
      y={contextMenu.y}
      options={menuOptions}
      onClose={closeContextMenu}
      isIgnoreErrorActive={isErrorIgnored}
    />
  );
};

export default ContextMenuWrapper;
